import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Container,
  DogByIDModal,
  EditDogModal,
  UpdateInvoiceModal,
  UpdateAppointmentTimeModal,
  AppointmentHistoryDrawer,
  LockerBoard,
} from '../../components/components';
import {
  Avatar,
  Button,
  Dropdown,
  Input,
  message,
  Segmented,
  Skeleton,
  Spin,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  SearchOutlined,
  HomeOutlined,
  LoginOutlined,
  LogoutOutlined,
  MedicineBoxFilled,
  MoreOutlined,
} from '@ant-design/icons';
import { PiDogFill } from 'react-icons/pi';
import dayjs from 'dayjs';
import { dashboard_white_icon } from '../../assets/icons/icons';
import {
  DailyOpsApi,
  useGetDailyOpsQuery,
  useUpdateLockerMutation,
} from '../../services/dailyOps';
import {
  useCheckinAppointmentMutation,
  useCheckoutAppointmentMutation,
  useLazyGetAppointmentByIdQuery,
  useUpdateAppointmentMutation,
} from '../../services/appointments';
import { useLazyGetDogByIDQuery } from '../../services/dogsApi';
import RefreshIndicator from '../../components/refreshIndicator/refreshIndicator';
import { usePolling } from '../../hooks/usePolling';
import { useAppointmentStatusChange } from '../../hooks/useAppointmentStatusChange';
import {
  EASTERN_TIMEZONE,
  formatDateET,
  formatTimeET,
  sortDogsByName,
} from '../../helpers/helpers';
import { APPOINTMENT_STATUS } from '../../constants/constants';

const BOARDING_COLOR = '#F59E0B';
const DAYCARE_COLOR = '#14B8A6';

/* The first three stat cards follow the Boarding / Daycare filter along with the
   table. Today's Activity, its breakdown bar, and the locker board always
   describe the whole day. */
const FILTERED_NOTE = 'Follows the service filter — matches the table below.';
const WHOLE_DAY_NOTE =
  'Covers the whole day, whichever service filter is selected.';

/* Compact summary of the appointment dates for display under the service tag.
   Boarding:  "Apr 17 → Apr 21"
   Daycare:   "Apr 19" | "Apr 19, Apr 22" | "Apr 19, Apr 22 +2" (tooltip has all) */
const formatAppointmentDates = row => {
  if (row.service?.is_boarding) {
    const start = row.appointment_start_time;
    const end = row.appointment_end_time;
    if (!start || !end) return null;
    return `${formatDateET(start, 'MMM D')} → ${formatDateET(end, 'MMM D')}`;
  }
  const dates = (row.appointment_dates || []).slice().sort();
  if (dates.length === 0) return null;
  // These are already plain YYYY-MM-DD calendar dates — no zone conversion.
  const formatted = dates.map(d => dayjs(d).format('MMM D'));
  if (formatted.length <= 2) return formatted.join(', ');
  return `${formatted.slice(0, 2).join(', ')} +${formatted.length - 2}`;
};

/* ── Top stat card ──────────────────────────────────────────── */
const StatCard = ({
  icon,
  label,
  hint,
  value,
  detail,
  accent,
  bg,
  border,
  loading,
  stale,
}) => (
  <div
    className="rounded-2xl p-5 flex items-center gap-4"
    style={{
      background: bg,
      border: `1px solid ${border}`,
      // A filter switch refetches: dim the number rather than let the previous
      // filter's figure sit there looking current.
      opacity: stale ? 0.6 : 1,
      transition: 'opacity 150ms ease',
    }}
  >
    <div
      className="h-12 w-12 rounded-xl flex items-center justify-center text-xl"
      style={{ background: `${accent}22`, color: accent }}
    >
      {icon}
    </div>
    <div className="min-w-0">
      <Tooltip title={hint}>
        <p className="text-xs font-medium tracking-wide uppercase text-gray-500 mb-0.5 inline-block">
          {label}
        </p>
      </Tooltip>
      <div className="flex items-baseline gap-1">
        {loading ? (
          <Skeleton.Button
            active
            size="small"
            style={{ width: 42, height: 28, minWidth: 42 }}
          />
        ) : (
          <p
            className="text-3xl font-bold leading-none"
            style={{ color: accent }}
          >
            {value ?? '—'}
          </p>
        )}
        <p className="text-sm text-gray-500">dogs</p>
      </div>
      {detail && !loading && (
        <p className="text-xs text-gray-500 mt-1">{detail}</p>
      )}
    </div>
  </div>
);

/* "4 of 7 arrived" — how far through the scheduled total the day is. Nothing
   when the API predates the scheduled counts or nothing is scheduled. */
const progressDetail = (remaining, scheduled, verb) =>
  typeof remaining === 'number' && scheduled > 0
    ? `${scheduled - remaining} of ${scheduled} ${verb}`
    : null;

/* ── Makeup of today ────────────────────────────────────────── */
/* A single stacked bar breaking the Today's Activity total into the three
   buckets the backend returns. Boarding keeps the amber it wears everywhere
   else on this page and daycare keeps its teal, so the bar reads against the
   table's row borders without a second colour vocabulary to learn. */
const TODAY_BUCKETS = [
  { key: 'boardingInHouse', label: 'Boarding · in house', color: '#B45309' },
  {
    key: 'boardingArriving',
    label: 'Boarding · arriving',
    color: BOARDING_COLOR,
  },
  { key: 'daycare', label: 'Daycare', color: DAYCARE_COLOR },
];

const bucketColor = key =>
  TODAY_BUCKETS.find(b => b.key === key)?.color || '#9ca3af';

const MAX_TOOLTIP_NAMES = 8;

const segmentTooltip = (segment, total) => {
  const share = total > 0 ? Math.round((segment.count / total) * 100) : 0;
  const names = segment.dogs || [];
  const shown = names.slice(0, MAX_TOOLTIP_NAMES).join(', ');
  const rest = names.length - MAX_TOOLTIP_NAMES;
  return (
    <div className="text-xs leading-relaxed">
      <div className="font-semibold">{segment.label}</div>
      <div>
        {segment.count} {segment.count === 1 ? 'dog' : 'dogs'} · {share}% of
        today
      </div>
      {shown && (
        <div className="mt-1 opacity-80">
          {shown}
          {rest > 0 ? ` +${rest} more` : ''}
        </div>
      )}
    </div>
  );
};

const TodayBreakdownBar = ({ breakdown, total, loading, stale }) => {
  // An older API response without the breakdown gets no empty card.
  if (!loading && !breakdown.length) return null;

  const filled = breakdown.filter(s => s.count > 0);

  return (
    <div
      className="bg-white rounded-2xl p-4 mb-4"
      style={{
        border: '1px solid #f0ebe6',
        opacity: stale ? 0.6 : 1,
        transition: 'opacity 150ms ease',
      }}
    >
      <Tooltip title={`Every dog in today's operation. ${WHOLE_DAY_NOTE}`}>
        <p className="text-xs font-medium tracking-wide uppercase text-gray-500 mb-2.5 inline-block">
          Makeup of today
        </p>
      </Tooltip>

      {loading ? (
        <Skeleton.Input active block style={{ height: 20, borderRadius: 4 }} />
      ) : filled.length === 0 ? (
        <div
          className="rounded"
          style={{ height: 20, background: '#f3f4f6' }}
          role="img"
          aria-label="No dogs scheduled today"
        />
      ) : (
        /* The 2px flex gap is the card surface showing through — the segments
           are separated by white space, never by a border. */
        <div
          className="flex overflow-hidden rounded"
          style={{ height: 20, gap: 2 }}
          role="img"
          aria-label={filled
            .map(s => `${s.count} ${s.label.replace(' · ', ' ')}`)
            .join(', ')}
        >
          {filled.map(segment => (
            <Tooltip key={segment.key} title={segmentTooltip(segment, total)}>
              <div
                style={{
                  flexGrow: segment.count,
                  flexBasis: 0,
                  minWidth: 6,
                  background: bucketColor(segment.key),
                }}
              />
            </Tooltip>
          ))}
        </div>
      )}

      {/* Legend doubles as the table view: every bucket carries its count in
          plain text, so no value is only reachable by hovering. Always all
          three, in fixed order, so a filter never repaints or reorders them. */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2.5">
        {TODAY_BUCKETS.map(bucket => {
          const segment = breakdown.find(s => s.key === bucket.key);
          return (
            <div key={bucket.key} className="flex items-center gap-1.5">
              <span
                className="rounded-full shrink-0"
                style={{ width: 8, height: 8, background: bucket.color }}
              />
              <span className="text-xs text-gray-500">
                {segment?.label || bucket.label}
              </span>
              <span className="text-xs font-semibold text-gray-900">
                {loading ? '—' : (segment?.count ?? 0)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Inline locker editor ───────────────────────────────────── */
/* Accepts a single value ("5"), a range ("1-3"), or a comma-separated
   list ("1,2,5") since a multi-dog appointment can occupy several lockers. */
const LOCKER_PATTERN = /^[\d,\-\s]+$/;

const LockerCell = ({ appointmentId, value, onSave }) => {
  const [draft, setDraft] = useState(value ?? '');

  React.useEffect(() => setDraft(value ?? ''), [value]);

  const commit = () => {
    const trimmed = (draft ?? '').toString().trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === (value ?? null)) return;
    if (next !== null && !LOCKER_PATTERN.test(next)) {
      message.warning(
        'Locker must be digits, commas, or dashes (e.g. 5, 1-3, 1,2,5)'
      );
      setDraft(value ?? '');
      return;
    }
    onSave(appointmentId, next);
  };

  return (
    <Input
      className="locker-input"
      size="small"
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onPressEnter={commit}
      placeholder="—"
      style={{ width: 80 }}
    />
  );
};

/* ── Row actions — shows both Check-in / Check-out like the reference ─ */
const RowAction = ({
  row,
  onCheckin,
  onCheckout,
  onViewAppointment,
  onModify,
  onViewLog,
}) => {
  const status = row.status;
  const isCheckedIn = status === APPOINTMENT_STATUS.CHECKED_IN;
  const isCheckedOut = status === APPOINTMENT_STATUS.CHECKED_OUT;
  const canCheckIn = status === APPOINTMENT_STATUS.ACCEPTED;
  const canModify =
    status !== APPOINTMENT_STATUS.CANCELLED &&
    status !== APPOINTMENT_STATUS.CHECKED_OUT;

  const menuItems = [
    { key: 'view-appointment', label: 'View Appointment' },
    ...(canModify
      ? [{ key: 'modify-appointment', label: 'Modify Appointment' }]
      : []),
    { key: 'view-log', label: 'View Log' },
  ];

  const onMenuClick = ({ key }) => {
    if (key === 'view-appointment') onViewAppointment(row.appointment_id);
    else if (key === 'modify-appointment') onModify(row.appointment_id);
    else if (key === 'view-log') onViewLog(row.appointment_id);
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Button
        size="small"
        icon={<LoginOutlined />}
        onClick={() => canCheckIn && onCheckin(row.appointment_id)}
        disabled={!canCheckIn}
        style={{
          borderRadius: 999,
          borderColor: canCheckIn ? '#d1d5db' : '#e5e7eb',
          color: canCheckIn ? '#111827' : '#9ca3af',
          background: '#fff',
          fontWeight: 500,
        }}
      >
        Check-in
      </Button>
      <Button
        size="small"
        icon={<LogoutOutlined />}
        onClick={() => isCheckedIn && onCheckout(row.appointment_id)}
        disabled={!isCheckedIn}
        style={{
          borderRadius: 999,
          borderColor: isCheckedIn ? '#fecaca' : '#e5e7eb',
          background: isCheckedIn ? '#fff1f2' : '#fff',
          color: isCheckedIn ? '#b91c1c' : isCheckedOut ? '#059669' : '#9ca3af',
          fontWeight: 500,
        }}
      >
        {isCheckedOut ? 'Checked out' : 'Check-out'}
      </Button>
      <Dropdown
        menu={{ items: menuItems, onClick: onMenuClick }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button
          size="small"
          icon={<MoreOutlined />}
          style={{
            borderRadius: 999,
            borderColor: '#d1d5db',
            background: '#fff',
          }}
        />
      </Dropdown>
    </div>
  );
};

/* ── Dashboard ──────────────────────────────────────────────── */
const Dashboard = () => {
  const [serviceFilter, setServiceFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // The day boundaries behind every count are Eastern, so ask for the ET date
  // rather than the browser's. Re-checked on an interval so a dashboard left
  // open overnight rolls over instead of pinning yesterday.
  const [today, setToday] = useState(() =>
    dayjs().tz(EASTERN_TIMEZONE).format('YYYY-MM-DD')
  );

  React.useEffect(() => {
    const t = setInterval(() => {
      const current = dayjs().tz(EASTERN_TIMEZONE).format('YYYY-MM-DD');
      setToday(prev => (prev === current ? prev : current));
    }, 60 * 1000);
    return () => clearInterval(t);
  }, []);

  // Debounce search input (matches existing pages/dogs debounce pattern)
  React.useEffect(() => {
    const t = setTimeout(
      () => setDebouncedSearch(search.trim().toLowerCase()),
      400
    );
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, isFetching, refetch } = useGetDailyOpsQuery(
    {
      date: today,
      service: serviceFilter === 'all' ? undefined : serviceFilter,
    },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  );

  // Reservations change from places this page can't see — the Appointments page,
  // the client app, another admin — so poll rather than waiting for a mutation
  // made here. No `paused` argument: unlike Appointments, the search below only
  // filters rows already fetched, so there is nothing to pause.
  const { manualRefresh } = usePolling(refetch, 15000);

  // After a change made here, invalidate rather than refetch(): RTK Query drops
  // a refetch while a poll is in flight, and that poll may have read the data
  // before the change landed. Invalidation waits for it, then fetches again.
  const dispatch = useDispatch();
  const refreshOps = useCallback(
    () => dispatch(DailyOpsApi.util.invalidateTags(['DailyOps'])),
    [dispatch]
  );

  const { handleStatusChange, updatingStatusIds } = useAppointmentStatusChange({
    // zIndex keeps the confirm dialog above the appointment detail modal
    zIndex: 2000,
    onSuccess: (newStatus, id) => {
      refreshOps();
      // The detail modal renders off the appointment fetched when it was
      // opened, so refreshing the table alone would leave it showing the
      // previous status.
      setInvoiceData(prev =>
        prev?._id === id ? { ...prev, status: newStatus } : prev
      );
    },
  });

  const [updateLocker] = useUpdateLockerMutation();
  const [checkinAppointment, { isLoading: isCheckingIn }] =
    useCheckinAppointmentMutation();
  const [checkoutAppointment, { isLoading: isCheckingOut }] =
    useCheckoutAppointmentMutation();

  // Dog detail modals
  const [dogModalOpen, setDogModalOpen] = useState(false);
  const [editDogOpen, setEditDogOpen] = useState(false);
  const [dogData, setDogData] = useState({});
  const [getDogByID, { isLoading: isFetchingDog }] = useLazyGetDogByIDQuery();

  // Appointment detail modal (uses UpdateInvoiceModal — full appointment view)
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceData, setInvoiceData] = useState({});
  const [getAppointmentById, { isLoading: isFetchingAppt }] =
    useLazyGetAppointmentByIdQuery();
  const [updateAppointment, { isLoading: isUpdatingAppt }] =
    useUpdateAppointmentMutation();

  // Modify appointment time modal
  const [modifyOpen, setModifyOpen] = useState(false);
  const [appointmentTime, setAppointmentTime] = useState(null);

  // Appointment audit log drawer
  const [historyAppointmentId, setHistoryAppointmentId] = useState(null);

  const openDogModal = async dogId => {
    if (!dogId) return;
    setDogModalOpen(true);
    const res = await getDogByID(dogId);
    setDogData(res?.data?.data || {});
  };

  const handleEditDogFromView = () => {
    setDogModalOpen(false);
    setEditDogOpen(true);
  };

  const handleCloseEditDog = () => {
    setEditDogOpen(false);
    setDogData({});
  };

  const openAppointmentModal = async appointmentId => {
    if (!appointmentId) return;
    setInvoiceOpen(true);
    const res = await getAppointmentById(appointmentId);
    setInvoiceData(res?.data?.data || {});
  };

  const onUpdateInvoice = async inv => {
    const body = { appointment: { ...inv } };
    const response = await updateAppointment({
      id: invoiceData?._id,
      body,
    });
    if (!response?.data?.error) {
      refreshOps();
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
  };

  const openModifyModal = async appointmentId => {
    if (!appointmentId) return;
    setModifyOpen(true);
    const res = await getAppointmentById(appointmentId);
    setAppointmentTime(res?.data?.data || null);
  };

  const onUpdateTime = async inv => {
    const body = { appointment: { ...inv } };
    const response = await updateAppointment({
      id: appointmentTime?._id,
      body,
    });
    if (!response?.data?.error) {
      message.success('Appointment Time Updated Successfully');
      refreshOps();
      // Keep the modal open so the admin can choose to send an updated invoice.
    } else {
      message.error(response?.data?.msg || 'Something went wrong!');
    }
    return response;
  };

  const rows = data?.data?.rows || [];
  const counts = data?.data?.counts || {};
  const breakdown = data?.data?.todayBreakdown || [];
  const lockerBoard = data?.data?.lockerBoard || null;

  const skeletonRows = useMemo(
    () =>
      Array.from({ length: 5 }, (_, i) => ({
        appointment_id: `skeleton-${i}`,
        _skeleton: true,
      })),
    []
  );

  // Client-side search filter on any dog name or owner name
  const filteredRows = useMemo(() => {
    if (!debouncedSearch) return rows;
    return rows.filter(r => {
      const dogs = (r.dogs || [])
        .map(d => (d.name || '').toLowerCase())
        .join(' ');
      const owner = (r.owner?.name || '').toLowerCase();
      return dogs.includes(debouncedSearch) || owner.includes(debouncedSearch);
    });
  }, [rows, debouncedSearch]);

  const handleSaveLocker = async (appointmentId, locker_number) => {
    try {
      const res = await updateLocker({
        id: appointmentId,
        locker_number,
      }).unwrap();
      if (res?.error) throw new Error(res.message);
      message.success('Locker updated');
    } catch (err) {
      message.error(
        err?.data?.message || err.message || 'Failed to update locker'
      );
    }
  };

  const handleCheckin = async id => {
    try {
      const res = await checkinAppointment({ id, body: {} }).unwrap();
      if (res?.error) throw new Error(res.message);
      message.success('Checked in');
      refreshOps();
    } catch (err) {
      message.error(err?.data?.message || err.message || 'Check-in failed');
      // Status may have changed server-side (race with another admin / poll).
      // Refresh so the row reflects the new state and the button greys out.
      refreshOps();
    }
  };

  const handleCheckout = async id => {
    try {
      const res = await checkoutAppointment({ id, body: {} }).unwrap();
      if (res?.error) throw new Error(res.message);
      message.success('Checked out');
      refreshOps();
    } catch (err) {
      message.error(err?.data?.message || err.message || 'Check-out failed');
      refreshOps();
    }
  };

  const columns = useMemo(
    () => [
      {
        title: 'Locker #',
        dataIndex: 'locker_number',
        key: 'locker_number',
        width: 90,
        sorter: (a, b) => {
          const parse = v => {
            const m = String(v ?? '').match(/\d+/);
            return m ? parseInt(m[0], 10) : Number.POSITIVE_INFINITY;
          };
          return parse(a.locker_number) - parse(b.locker_number);
        },
        sortDirections: ['ascend', 'descend'],
        onCell: row => ({
          style: {
            borderLeft: `4px solid ${
              row.service?.is_boarding ? BOARDING_COLOR : DAYCARE_COLOR
            }`,
            paddingLeft: 12,
          },
        }),
        render: (value, row) => {
          if (row._skeleton) {
            return (
              <Skeleton.Button
                active
                size="small"
                style={{ width: 40, minWidth: 40 }}
              />
            );
          }
          return (
            <LockerCell
              appointmentId={row.appointment_id}
              value={value}
              onSave={handleSaveLocker}
            />
          );
        },
      },
      {
        title: 'Dogs',
        key: 'dogs',
        sorter: (a, b) => {
          const firstName = row =>
            (row.dogs || []).map(d => (d.name || '').toLowerCase()).sort()[0] ??
            '￿';
          return firstName(a).localeCompare(firstName(b));
        },
        sortDirections: ['ascend', 'descend'],
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <div className="flex items-center gap-3">
                <Skeleton.Avatar active size={36} shape="circle" />
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 110, height: 16 }}
                />
              </div>
            );
          }
          const dogs = sortDogsByName(row.dogs);
          if (dogs.length === 0) {
            return <span className="text-gray-400">—</span>;
          }
          return (
            <div className="flex flex-col gap-1.5">
              {dogs.map(dog => (
                <div
                  key={dog._id}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => openDogModal(dog._id)}
                >
                  <Avatar
                    src={dog.avatar}
                    icon={!dog.avatar && <PiDogFill />}
                    size={28}
                  />
                  <span className="font-medium">{dog.name || '—'}</span>
                  {dog.medication_required && (
                    <Tooltip title="Medication required">
                      <MedicineBoxFilled
                        style={{ color: '#c026d3', fontSize: 12 }}
                      />
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        title: 'Owner',
        key: 'owner',
        sorter: (a, b) =>
          (a.owner?.name || '').localeCompare(b.owner?.name || ''),
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <Skeleton.Input
                active
                size="small"
                style={{ width: 120, height: 16 }}
              />
            );
          }
          return <span>{row.owner?.name || '—'}</span>;
        },
      },
      {
        title: 'Service',
        key: 'service',
        width: 160,
        sorter: (a, b) =>
          (a.service?.title || '').localeCompare(b.service?.title || ''),
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <Skeleton.Button
                active
                size="small"
                style={{ width: 80, height: 22, minWidth: 80 }}
              />
            );
          }
          const color = row.service?.is_boarding
            ? BOARDING_COLOR
            : DAYCARE_COLOR;
          const datesLabel = formatAppointmentDates(row);
          return (
            <div className="flex flex-col gap-1 items-start">
              <Tag
                color={color}
                style={{
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  fontSize: 11,
                  margin: 0,
                }}
              >
                {row.service?.is_boarding ? 'Boarding' : 'Daycare'}
              </Tag>
              {datesLabel && (
                <Tooltip
                  title={
                    row.service?.is_boarding
                      ? undefined
                      : (row.appointment_dates || [])
                          .map(d => dayjs(d).format('ddd MMM D'))
                          .join(', ')
                  }
                >
                  <span className="text-xs text-gray-500">{datesLabel}</span>
                </Tooltip>
              )}
              {row.day_progress && (
                <span className="text-xs text-gray-400">
                  Day {row.day_progress.current}/{row.day_progress.total}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: 'Check-in',
        key: 'check_in',
        width: 150,
        sorter: (a, b) => {
          const av = a.check_in_time || a.appointment_start_time || '';
          const bv = b.check_in_time || b.appointment_start_time || '';
          return av.localeCompare(bv);
        },
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <div className="flex flex-col gap-1">
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 50, height: 12 }}
                />
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 80, height: 14 }}
                />
              </div>
            );
          }
          const actual = row.check_in_time;
          const scheduled = row.appointment_start_time;
          const value = actual || scheduled;
          if (!value) return <span className="text-gray-400">—</span>;
          return (
            <div className="flex flex-col leading-tight">
              <span className="text-xs text-gray-500">
                {formatDateET(value, 'MMM D')}
              </span>
              <span className={actual ? 'text-emerald-600 font-medium' : ''}>
                {formatTimeET(value)}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Check-out',
        key: 'check_out',
        width: 150,
        sorter: (a, b) => {
          const av = a.check_out_time || a.appointment_end_time || '';
          const bv = b.check_out_time || b.appointment_end_time || '';
          return av.localeCompare(bv);
        },
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <div className="flex flex-col gap-1">
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 50, height: 12 }}
                />
                <Skeleton.Input
                  active
                  size="small"
                  style={{ width: 80, height: 14 }}
                />
              </div>
            );
          }
          const actual = row.check_out_time;
          const scheduled = row.appointment_end_time;
          const value = actual || scheduled;
          if (!value) return <span className="text-gray-400">—</span>;
          return (
            <div className="flex flex-col leading-tight">
              <span className="text-xs text-gray-500">
                {formatDateET(value, 'MMM D')}
              </span>
              <span className={actual ? 'text-rose-600 font-medium' : ''}>
                {formatTimeET(value)}
              </span>
            </div>
          );
        },
      },
      {
        title: 'Medication',
        key: 'medication',
        width: 120,
        align: 'center',
        sorter: (a, b) =>
          Number(b.medication_required) - Number(a.medication_required),
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <Skeleton.Input
                active
                size="small"
                style={{ width: 24, height: 14 }}
              />
            );
          }
          return row.medication_required ? (
            <Tooltip title="Medication required">
              <Tag
                color="magenta"
                style={{ borderRadius: 999, margin: 0, padding: '0 10px' }}
              >
                <MedicineBoxFilled /> Meds
              </Tag>
            </Tooltip>
          ) : (
            <span className="text-gray-300">—</span>
          );
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 180,
        align: 'right',
        render: (_, row) => {
          if (row._skeleton) {
            return (
              <div className="flex items-center justify-end gap-1.5">
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 84, height: 24, borderRadius: 999 }}
                />
                <Skeleton.Button
                  active
                  size="small"
                  style={{ width: 90, height: 24, borderRadius: 999 }}
                />
                <Skeleton.Avatar active size={24} shape="circle" />
              </div>
            );
          }
          return (
            <RowAction
              row={row}
              onCheckin={handleCheckin}
              onCheckout={handleCheckout}
              onViewAppointment={openAppointmentModal}
              onModify={openModifyModal}
              onViewLog={setHistoryAppointmentId}
            />
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const busy = isCheckingIn || isCheckingOut;
  // Data on screen is a filter/day behind while the next response is in flight.
  const stale = isFetching && !isLoading;

  return (
    <Container
      selected={'1'}
      header={
        <div className="flex items-center gap-4">
          <div className="flex items-center">
            <div className="bg-primary h-9 w-9 rounded-full flex justify-center items-center">
              <img src={dashboard_white_icon} width={17} height={17} alt="" />
            </div>
            <p className="ml-3 font-semibold text-2xl">Dashboard</p>
            <span className="ml-3 text-sm text-gray-400">
              {dayjs(today).format('dddd, MMMM D, YYYY')}
            </span>
          </div>
          <RefreshIndicator onManualRefresh={manualRefresh} />
        </div>
      }
    >
      <div className="p-6">
        {/* ── Top stat cards ─────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<HomeOutlined />}
            label="In House"
            hint={`Dogs checked in right now. ${FILTERED_NOTE}`}
            value={counts.inHouse}
            accent="#B45309"
            bg="#FEF3C7"
            border="#FDE68A"
            loading={isLoading}
            stale={stale}
          />
          <StatCard
            icon={<LoginOutlined />}
            label="Coming Today"
            hint={`Dogs due today who haven't checked in yet. ${FILTERED_NOTE}`}
            value={counts.comingToday}
            detail={progressDetail(
              counts.comingToday,
              counts.comingTodayScheduled,
              'arrived'
            )}
            accent="#C2410C"
            bg="#FFEDD5"
            border="#FED7AA"
            loading={isLoading}
            stale={stale}
          />
          <StatCard
            icon={<LogoutOutlined />}
            label="Leaving Today"
            hint={`Dogs due to go home today who haven't checked out yet. ${FILTERED_NOTE}`}
            value={counts.leavingToday}
            detail={progressDetail(
              counts.leavingToday,
              counts.leavingTodayScheduled,
              'gone home'
            )}
            accent="#B91C1C"
            bg="#FEE2E2"
            border="#FECACA"
            loading={isLoading}
            stale={stale}
          />
          <StatCard
            icon={<PiDogFill />}
            label="Today's Activity"
            hint={`Every dog in today's operation — boarding dogs in house, boarding dogs still to arrive today, and daycare. Counts each dog once, and stays put when a dog checks out. ${WHOLE_DAY_NOTE}`}
            value={counts.todayActivity}
            accent="#4338CA"
            bg="#EEF2FF"
            border="#C7D2FE"
            loading={isLoading}
            stale={stale}
          />
        </div>

        {/* ── Makeup of today ────────────────────────────── */}
        <TodayBreakdownBar
          breakdown={breakdown}
          total={counts.todayActivity}
          loading={isLoading}
          stale={stale}
        />

        {/* ── Locker board ───────────────────────────────── */}
        <LockerBoard
          board={lockerBoard}
          loading={isLoading}
          stale={stale}
          onRangeChange={refreshOps}
        />

        {/* ── Filter bar ─────────────────────────────────── */}
        <div
          className="bg-white rounded-2xl p-3 mb-4 flex flex-wrap items-center gap-3"
          style={{ border: '1px solid #f0ebe6' }}
        >
          <Segmented
            value={serviceFilter}
            onChange={setServiceFilter}
            options={[
              { label: 'All', value: 'all' },
              { label: 'Boarding', value: 'boarding' },
              { label: 'Daycare', value: 'daycare' },
            ]}
          />
          <Input
            prefix={<SearchOutlined className="text-gray-400" />}
            placeholder="Search dog or owner…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            allowClear
            style={{ maxWidth: 320 }}
          />
          {/* Refetches are the poller's to report — RefreshIndicator owns that
              signal, and the stale dimming above covers it. This is left for
              check-in/out, which has no other spinner. */}
          {busy && <Spin size="small" className="ml-auto" />}
        </div>

        {/* ── Table ──────────────────────────────────────── */}
        <div
          className="dashboard-table bg-white rounded-2xl overflow-hidden"
          style={{ border: '1px solid #f0ebe6' }}
        >
          <Table
            rowKey={record => record.appointment_id}
            columns={columns}
            dataSource={isLoading ? skeletonRows : filteredRows}
            pagination={
              isLoading
                ? false
                : {
                    defaultPageSize: 40,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '40', '80'],
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} appointments`,
                    hideOnSinglePage: true,
                  }
            }
            locale={{
              emptyText: isLoading
                ? ' '
                : 'No appointments scheduled for today',
            }}
          />
        </div>
      </div>

      <DogByIDModal
        isOpen={dogModalOpen}
        onClose={() => {
          setDogModalOpen(false);
          setDogData({});
        }}
        data={dogData}
        isLoading={isFetchingDog}
        onEditDog={handleEditDogFromView}
      />
      <EditDogModal
        isOpen={editDogOpen}
        onClose={handleCloseEditDog}
        data={dogData}
        onSuccess={refreshOps}
      />
      <UpdateInvoiceModal
        isOpen={invoiceOpen}
        onClose={() => {
          setInvoiceOpen(false);
          setInvoiceData({});
        }}
        data={invoiceData}
        setData={setInvoiceData}
        onSubmit={onUpdateInvoice}
        isLoading={isFetchingAppt || isUpdatingAppt}
        onInvoiceSent={refreshOps}
        onModify={appt => {
          setAppointmentTime(appt);
          setModifyOpen(true);
        }}
        onViewHistory={id => setHistoryAppointmentId(id)}
        onStatusChange={handleStatusChange}
        isStatusUpdating={updatingStatusIds.has(invoiceData?._id)}
      />
      <UpdateAppointmentTimeModal
        isOpen={modifyOpen}
        onClose={() => {
          setModifyOpen(false);
          setAppointmentTime(null);
        }}
        isLoading={isFetchingAppt || isUpdatingAppt}
        onClick={onUpdateTime}
        appointmentTime={appointmentTime}
        btnText={'Modify Stay'}
        onInvoiceSent={refreshOps}
        zIndex={1050}
      />
      <AppointmentHistoryDrawer
        isOpen={!!historyAppointmentId}
        onClose={() => setHistoryAppointmentId(null)}
        appointmentId={historyAppointmentId}
        onOpenDog={openDogModal}
        zIndex={1050}
      />
    </Container>
  );
};

export default Dashboard;
